import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, CheckCircle, Clock, XCircle } from 'lucide-react';
import { toast } from 'sonner';

export const GoogleCallback = ({ onLogin }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing your authentication...');
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent multiple executions
    if (hasProcessed.current) return;
    
    const callbackStatus = searchParams.get('status');
    const callbackMessage = searchParams.get('message');
    const token = searchParams.get('token');
    const email = searchParams.get('email');
    const name = searchParams.get('name');

    if (!callbackStatus) {
      hasProcessed.current = true;
      setStatus('error');
      setMessage('Invalid authentication response');
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    hasProcessed.current = true;
    setStatus(callbackStatus);

    switch (callbackStatus) {
      case 'success':
        // Login successful
        if (token && email) {
          const user = {
            email: email,
            name: name || email.split('@')[0],
            api_key: token
          };
          localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(user));
          onLogin(token, user);
          setMessage('Login successful! Redirecting...');
          toast.success('Welcome back!');
          setTimeout(() => navigate('/dashboard'), 1500);
        } else {
          setStatus('error');
          setMessage('Authentication failed');
          setTimeout(() => navigate('/login'), 2000);
        }
        break;

      case 'pending':
        // Account pending approval
        setMessage(callbackMessage || 'Your account is pending admin approval');
        toast.info('Account pending approval', {
          description: 'An admin will review and activate your account soon.'
        });
        setTimeout(() => navigate('/login'), 4000);
        break;

      case 'created':
        // New account created
        setMessage(callbackMessage || 'Account created successfully. Waiting for admin approval.');
        toast.success('Account created!', {
          description: 'An admin will activate your account soon.'
        });
        setTimeout(() => navigate('/login'), 4000);
        break;

      case 'error':
        // Error occurred
        setMessage(callbackMessage || 'Authentication failed. Please try again.');
        toast.error('Authentication failed');
        setTimeout(() => navigate('/login'), 3000);
        break;

      default:
        setStatus('error');
        setMessage('Unknown status');
        setTimeout(() => navigate('/login'), 2000);
    }
  }, [searchParams, navigate, onLogin, setStatus, setMessage]);

  const getIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-16 w-16 text-green-500" />;
      case 'pending':
      case 'created':
        return <Clock className="h-16 w-16 text-yellow-500" />;
      case 'error':
        return <XCircle className="h-16 w-16 text-red-500" />;
      default:
        return <Loader2 className="h-16 w-16 text-blue-500 animate-spin" />;
    }
  };

  const getColor = () => {
    switch (status) {
      case 'success':
        return 'from-green-50 via-white to-emerald-50';
      case 'pending':
      case 'created':
        return 'from-yellow-50 via-white to-amber-50';
      case 'error':
        return 'from-red-50 via-white to-rose-50';
      default:
        return 'from-blue-50 via-white to-indigo-50';
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${getColor()} flex items-center justify-center`} data-testid="google-callback-page">
      <div className="container mx-auto px-4 py-8 max-w-md">
        <Card>
          <CardContent className="pt-8 pb-8">
            <div className="flex flex-col items-center text-center space-y-4">
              {getIcon()}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {status === 'success' && 'Success!'}
                  {status === 'pending' && 'Account Pending'}
                  {status === 'created' && 'Account Created!'}
                  {status === 'error' && 'Error'}
                  {status === 'processing' && 'Processing...'}
                </h2>
                <p className="text-gray-600">{message}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
